import { useState, useEffect } from 'react';
import {
  X, HeartHandshake, User, Phone, Mail, Calendar, Activity,
  FileText, DollarSign, Clock, ShieldAlert, Sparkles, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import opheService from '../../services/ophe.service';

export default function OpheLeadCreateModal({ isOpen, onClose, onSuccess }) {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    patientAge: '',
    patientGender: 'male',
    patientAddress: '',
    emergencyContact: '',
    alternatePhone: '',
    priority: 'medium',
    notes: '',
    // OPHE Service Selection
    categoryId: '',
    serviceId: '',
    duration: 'Day',
    quotedPrice: 0,
    startDate: '',
    endDate: '',
    // Vitals & Intake
    conditionNotes: '',
    vitalBloodPressure: '',
    vitalSugar: '',
    vitalOxygenSpo2: '',
    vitalHeartRate: '',
    mobilityStatus: 'Independent',
    dietaryRequirements: '',
    primaryDiagnosis: '',
    allergies: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadCatalog();
    }
  }, [isOpen]);

  const loadCatalog = async () => {
    try {
      setLoadingCatalog(true);
      const [catRes, servRes] = await Promise.all([
        opheService.getCategories({ status: 'active' }),
        opheService.getServices({ status: 'active' })
      ]);
      setCategories(catRes.data?.data || []);
      setServices(servRes.data?.data || []);
    } catch (err) {
      toast.error('Failed to load OPHE services catalog');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // Filter services by category
  const filteredServices = formData.categoryId
    ? services.filter(s => {
        const catId = typeof s.categoryId === 'object' ? s.categoryId?._id : s.categoryId;
        return catId === formData.categoryId;
      })
    : services;

  const handleServiceChange = (serviceId) => {
    const selected = services.find(s => s._id === serviceId);
    if (selected) {
      const catId = typeof selected.categoryId === 'object' ? selected.categoryId?._id : selected.categoryId;
      const priceForDuration = selected.durationPricing?.[formData.duration] || selected.basePrice || 0;
      setFormData(prev => ({
        ...prev,
        serviceId,
        categoryId: catId || prev.categoryId,
        quotedPrice: priceForDuration
      }));
    } else {
      setFormData(prev => ({ ...prev, serviceId, quotedPrice: 0 }));
    }
  };

  const handleDurationChange = (duration) => {
    const selected = services.find(s => s._id === formData.serviceId);
    const newPrice = selected?.durationPricing?.[duration] || formData.quotedPrice;
    setFormData(prev => ({
      ...prev,
      duration,
      quotedPrice: newPrice
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patientName.trim() || !formData.patientPhone.trim()) {
      toast.error('Patient Name and Phone number are required');
      return;
    }

    try {
      setSubmitting(true);
      const selectedService = services.find(s => s._id === formData.serviceId);

      const payload = {
        patientName: formData.patientName.trim(),
        patientPhone: formData.patientPhone.trim(),
        patientEmail: formData.patientEmail.trim() || undefined,
        patientAge: formData.patientAge ? Number(formData.patientAge) : undefined,
        patientGender: formData.patientGender,
        patientAddress: formData.patientAddress.trim() || undefined,
        emergencyContact: formData.emergencyContact.trim() || undefined,
        priority: formData.priority,
        notes: formData.notes,
        opheServiceDetails: {
          categoryId: formData.categoryId || undefined,
          serviceId: formData.serviceId || undefined,
          duration: formData.duration,
          packageName: selectedService?.name || '',
          quotedPrice: Number(formData.quotedPrice) || 0,
          startDate: formData.startDate || undefined,
          endDate: formData.endDate || undefined,
          serviceNotes: formData.notes
        },
        opheMedicalInfo: {
          conditionNotes: formData.conditionNotes,
          vitalBloodPressure: formData.vitalBloodPressure,
          vitalSugar: formData.vitalSugar,
          vitalOxygenSpo2: formData.vitalOxygenSpo2,
          vitalHeartRate: formData.vitalHeartRate,
          mobilityStatus: formData.mobilityStatus,
          dietaryRequirements: formData.dietaryRequirements,
          primaryDiagnosis: formData.primaryDiagnosis,
          allergies: formData.allergies
        }
      };

      const res = await opheService.createLead(payload);
      toast.success('OPHE Lead created successfully!');
      if (onSuccess) onSuccess(res.data?.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create OPHE lead');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-100 my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-700 via-teal-800 to-cyan-900 px-6 py-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-md border border-white/20">
              <HeartHandshake className="w-6 h-6 text-teal-200" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">New OPHE Care Request</h2>
              <p className="text-xs text-teal-200/90">Elderly & Post-Hospitalization Home Care Intake</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[80vh] overflow-y-auto space-y-6">
          
          {/* Section 1: Service Package Selection */}
          <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-teal-800 font-bold text-sm">
              <Sparkles className="w-4 h-4 text-teal-600" />
              <span>1. OPHE Package & Care Duration</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Care Category</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData(p => ({ ...p, categoryId: e.target.value, serviceId: '' }))}
                  className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                >
                  <option value="">-- All Categories --</option>
                  {categories.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Service */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Service Package *</label>
                <select
                  value={formData.serviceId}
                  onChange={(e) => handleServiceChange(e.target.value)}
                  className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                >
                  <option value="">-- Select Service Package --</option>
                  {filteredServices.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Duration</label>
                <div className="grid grid-cols-4 gap-1">
                  {['Day', '7 Days', '15 Days', '30 Days'].map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => handleDurationChange(d)}
                      className={`px-2 py-2 text-xs font-medium rounded-lg border transition-all ${
                        formData.duration === d
                          ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-teal-50'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Pricing & Start Date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-teal-100">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Quoted Care Price (₹)</label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="number"
                    value={formData.quotedPrice}
                    onChange={(e) => setFormData(p => ({ ...p, quotedPrice: e.target.value }))}
                    className="w-full text-sm pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white font-bold text-teal-800 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Requested Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(p => ({ ...p, startDate: e.target.value }))}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                >
                  <option value="low">Standard / Low</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">Urgent / High</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Patient & Contact Information */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
              <User className="w-4 h-4 text-teal-600" />
              <span>2. Patient & Family Contact Details</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Patient Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.patientName}
                  onChange={(e) => setFormData(p => ({ ...p, patientName: e.target.value }))}
                  placeholder="e.g. Ramesh Chandra Sharma"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Primary Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.patientPhone}
                  onChange={(e) => setFormData(p => ({ ...p, patientPhone: e.target.value }))}
                  placeholder="10-digit mobile number"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address (Optional)</label>
                <input
                  type="email"
                  value={formData.patientEmail}
                  onChange={(e) => setFormData(p => ({ ...p, patientEmail: e.target.value }))}
                  placeholder="patient@gmail.com"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Age</label>
                <input
                  type="number"
                  value={formData.patientAge}
                  onChange={(e) => setFormData(p => ({ ...p, patientAge: e.target.value }))}
                  placeholder="e.g. 72"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Gender</label>
                <select
                  value={formData.patientGender}
                  onChange={(e) => setFormData(p => ({ ...p, patientGender: e.target.value }))}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Emergency / Family Contact</label>
                <input
                  type="text"
                  value={formData.emergencyContact}
                  onChange={(e) => setFormData(p => ({ ...p, emergencyContact: e.target.value }))}
                  placeholder="Name & Contact (e.g. Son: 9876543210)"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-700 mb-1">Home Care Delivery Address</label>
                <textarea
                  rows={2}
                  value={formData.patientAddress}
                  onChange={(e) => setFormData(p => ({ ...p, patientAddress: e.target.value }))}
                  placeholder="Complete home address with landmark and city..."
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Vitals & Medical Assessment */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
              <Activity className="w-4 h-4 text-rose-600" />
              <span>3. Medical Conditions & Vitals Intake</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">BP (mmHg)</label>
                <input
                  type="text"
                  value={formData.vitalBloodPressure}
                  onChange={(e) => setFormData(p => ({ ...p, vitalBloodPressure: e.target.value }))}
                  placeholder="120/80"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Sugar (mg/dL)</label>
                <input
                  type="text"
                  value={formData.vitalSugar}
                  onChange={(e) => setFormData(p => ({ ...p, vitalSugar: e.target.value }))}
                  placeholder="140"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">SpO2 (%)</label>
                <input
                  type="text"
                  value={formData.vitalOxygenSpo2}
                  onChange={(e) => setFormData(p => ({ ...p, vitalOxygenSpo2: e.target.value }))}
                  placeholder="98%"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Heart Rate (bpm)</label>
                <input
                  type="text"
                  value={formData.vitalHeartRate}
                  onChange={(e) => setFormData(p => ({ ...p, vitalHeartRate: e.target.value }))}
                  placeholder="74"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Mobility Status</label>
                <select
                  value={formData.mobilityStatus}
                  onChange={(e) => setFormData(p => ({ ...p, mobilityStatus: e.target.value }))}
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                >
                  <option value="Independent">Independent</option>
                  <option value="Needs Assistance">Needs Assistance / Walker</option>
                  <option value="Wheelchair Bound">Wheelchair Bound</option>
                  <option value="Bedridden">Bedridden</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Primary Diagnosis</label>
                <input
                  type="text"
                  value={formData.primaryDiagnosis}
                  onChange={(e) => setFormData(p => ({ ...p, primaryDiagnosis: e.target.value }))}
                  placeholder="e.g. Post Knee Replacement"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Diet / Special Care Needs</label>
                <input
                  type="text"
                  value={formData.dietaryRequirements}
                  onChange={(e) => setFormData(p => ({ ...p, dietaryRequirements: e.target.value }))}
                  placeholder="e.g. Diabetic Diet, Catheter Care, Dressing"
                  className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>

              <div className="col-span-2 md:col-span-4">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Care Notes & Special Instructions</label>
                <textarea
                  rows={2}
                  value={formData.conditionNotes}
                  onChange={(e) => setFormData(p => ({ ...p, conditionNotes: e.target.value }))}
                  placeholder="Any specific care routine, medication schedule, or family instructions..."
                  className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 active:bg-teal-800 rounded-xl shadow-lg shadow-teal-600/20 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <span>Creating OPHE Case...</span>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Create OPHE Care Case</span>
                </>
              )}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
}
